import { api } from './api'

/**
 * Request to create a payment via Stripe Checkout
 */
export interface CreatePaymentRequest {
  venueId: string
  amountCents: number
  tipCents: number
  receiptId?: string
  customerEmail?: string
  description?: string
  source: 'receipt' | 'manual'
}

/**
 * Response from payment creation
 */
export interface CreatePaymentResponse {
  paymentId: string
  status: string
  checkoutUrl: string
  checkoutSessionId: string
  amountCents: number
  tipCents: number
  totalCents: number
  currency: string
  createdAt: string
}

/**
 * API response format for create payment
 */
interface ApiPaymentResponse {
  payment_id: string
  status: string
  checkout_url: string
  checkout_session_id: string
  amount_cents: number
  tip_cents: number
  total_cents: number
  currency: string
  created_at: string
}

/**
 * Create a payment and get Stripe Checkout URL
 */
export async function createPayment(
  request: CreatePaymentRequest
): Promise<CreatePaymentResponse> {
  // Build success/cancel URLs with current origin
  const origin = window.location.origin
  const successUrl = `${origin}/success?payment_id={CHECKOUT_SESSION_ID}`
  const cancelUrl = `${origin}/capture`

  const data = await api.post<ApiPaymentResponse>('payments', {
    venue_id: request.venueId,
    amount_cents: request.amountCents,
    tip_cents: request.tipCents,
    success_url: successUrl,
    cancel_url: cancelUrl,
    receipt_id: request.receiptId,
    customer_email: request.customerEmail,
    line_items_description: request.description,
    currency: 'USD',
    source: request.source,
  })

  return {
    paymentId: data.payment_id,
    status: data.status,
    checkoutUrl: data.checkout_url,
    checkoutSessionId: data.checkout_session_id,
    amountCents: data.amount_cents,
    tipCents: data.tip_cents,
    totalCents: data.total_cents,
    currency: data.currency,
    createdAt: data.created_at,
  }
}

/**
 * Response from transaction endpoint (camelCase for frontend use)
 */
export interface TransactionResponse {
  id: string
  status: string
  totalCents: number
  currency: string
  createdAt: string
  receiptAmountCents: number
  company: {
    name: string
  }
}

/**
 * API response format for transaction (snake_case from backend)
 */
interface ApiTransactionResponse {
  id: string
  status: string
  total_cents: number
  currency: string
  created_at: string
  receipt_amount_cents: number
  company: {
    name: string
  }
}

/**
 * Get transaction details
 */
export async function getTransaction(transactionId: string): Promise<TransactionResponse> {
  const data = await api.get<ApiTransactionResponse>(`transactions/${transactionId}`)

  return {
    id: data.id,
    status: data.status,
    totalCents: data.total_cents,
    currency: data.currency,
    createdAt: data.created_at,
    receiptAmountCents: data.receipt_amount_cents,
    company: data.company,
  }
}

/**
 * Redirect to Stripe Checkout
 */
export function redirectToCheckout(checkoutUrl: string): void {
  // Validate it's a legitimate Stripe checkout URL
  const url = new URL(checkoutUrl)
  if (url.hostname !== 'checkout.stripe.com') {
    throw new Error('Invalid checkout URL')
  }
  window.location.href = checkoutUrl
}
